/**
 * `AccountDetailTabs` — the horizontal tab bar used at the top of the
 * account detail view.
 *
 * Styling mirrors the pattern already in use in `TrustDisputesView`
 * + `AgentOperatorsView`: mono-uppercase labels, 2px bottom border
 * for the active tab, no background. Thin and dense per CLAUDE.md
 * §4. Scrolls horizontally when it overflows the viewport (390px
 * mobile) so every tab stays reachable without a hamburger.
 *
 * Semantics:
 *   - `role="tablist"` on the container with an aria-label.
 *   - `role="tab"` + `aria-selected` + `aria-controls` on each
 *     button, pointing at the panel ID the parent renders.
 *   - The `coral` tone for Danger zone is a persistent colour cue;
 *     the active-tab underline sits over the top in text colour so
 *     the "I'm on the danger tab" state still reads cleanly.
 */

import { useEffect, useRef, type KeyboardEvent } from 'react';

import { useC } from '../../lib/theme.js';
import { ACCOUNT_TABS, type AccountTabId } from './account-detail-tabs.js';

const monoFont = "'JetBrains Mono', monospace";

export interface AccountDetailTabsProps {
  value: AccountTabId;
  onChange: (next: AccountTabId) => void;
  /** Prefix used to build each panel's `id` attribute, so
   *  `aria-controls` points somewhere real. */
  panelIdPrefix: string;
}

export function AccountDetailTabs({
  value,
  onChange,
  panelIdPrefix,
}: AccountDetailTabsProps): JSX.Element {
  const C = useC();
  const listRef = useRef<HTMLDivElement>(null);

  // On mount + when the active tab changes, nudge the horizontally
  // scrollable tab bar so the active tab is visible. On 1440 this is
  // a no-op (every tab already fits); on 390 the Danger tab lives off
  // the right edge of the viewport and would otherwise be invisible
  // when deep-linked via `?tab=danger`.
  useEffect(() => {
    const list = listRef.current;
    if (list === null) return;
    const btn = list.querySelector<HTMLElement>(`[aria-selected="true"]`);
    if (btn === null) return;
    // Only scroll when there's actual overflow; otherwise we'd pointlessly
    // trigger smooth-scroll animations on desktop.
    if (list.scrollWidth <= list.clientWidth) return;
    btn.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [value]);

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, idx: number): void => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') {
      return;
    }
    e.preventDefault();
    const last = ACCOUNT_TABS.length - 1;
    let nextIdx = idx;
    if (e.key === 'ArrowRight') nextIdx = idx === last ? 0 : idx + 1;
    else if (e.key === 'ArrowLeft') nextIdx = idx === 0 ? last : idx - 1;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = last;
    const target = ACCOUNT_TABS[nextIdx];
    if (!target) return;
    onChange(target.id);
    // Move focus to the newly-selected tab button for keyboard flow.
    if (typeof document !== 'undefined') {
      const el = document.getElementById(`${panelIdPrefix}-tab-${target.id}`);
      if (el instanceof HTMLElement) el.focus();
    }
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label="Account sections"
      style={{
        display: 'flex',
        gap: 2,
        marginTop: 20,
        marginBottom: 20,
        borderBottom: `1px solid ${C.borderSoft}`,
        overflowX: 'auto',
        // Hide the scrollbar on WebKit; the overflow is still swipeable.
        scrollbarWidth: 'thin',
      }}
    >
      {ACCOUNT_TABS.map((t, idx) => {
        const selected = value === t.id;
        const isDanger = t.id === 'danger';
        // Colour precedence:
        //   active, danger    → text + coral underline
        //   active, not danger→ text + text underline
        //   inactive, danger  → muted coral tint
        //   inactive          → muted
        const base = isDanger ? C.coralFg : selected ? C.text : C.muted;
        const underlineColor = selected
          ? isDanger
            ? C.coralFg
            : C.text
          : 'transparent';
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`${panelIdPrefix}-tab-${t.id}`}
            aria-selected={selected}
            aria-controls={`${panelIdPrefix}-panel-${t.id}`}
            tabIndex={selected ? 0 : -1}
            title={t.hint}
            onClick={() => onChange(t.id)}
            onKeyDown={(e) => onKeyDown(e, idx)}
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              padding: '10px 12px',
              background: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${underlineColor}`,
              color: selected ? (isDanger ? C.coralFg : C.text) : base,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              minHeight: 40,
              fontWeight: selected ? 600 : 400,
              // Firefox adds a dotted focus ring on button; keep the
              // visible focus-ring but match the admin's shape.
              outlineOffset: 2,
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
